import React, {
  useCallback, useContext, useEffect, useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import cx from 'classnames';

import { MDText } from 'i18n-react';

import { VRM as THCVRM } from '@motorway/mw-highway-code/alpha';

import { VehicleLookupContext } from 'Context/vehicleLookup';

import { type HeroAnimationState, HERO_ANIMATION_STATES } from 'Utilities/animation';

import { CONTENT_SIZES } from 'Layout';

import { fireCookieConsentTakeover } from 'Components/CookieBanner/cookieConsent.constants';
import { LogoAffiliate } from 'Storybook/components/local/LogoAffiliate/LogoAffiliate';
import { AffiliateType } from 'Storybook/components/local/LogoAffiliate/LogoAffiliate.helpers';

import VrmErrors from '../../../contentPages/ulezPage/VrmErrors/VrmErrors';
import VrmInput from '../../../fastlane/vrmInput/VrmInput';
import LocalTexts from '../AffiliatePageTexts.json';

import { CheckableErrorCode, errorCodesToCheck } from './VRM.helpers';

import styles from './VRM.scss';

const LocalT = new MDText(LocalTexts);

const VRM = ({ affiliate }: {affiliate: AffiliateType}) => {
  const history = useHistory();
  const { vehicleLookupState: vehicle } = useContext(VehicleLookupContext);
  const [showMileage, setShowMileage] = useState(false);
  const [loading, setLoading] = useState<HeroAnimationState>(HERO_ANIMATION_STATES.none);
  const isTransition = loading === HERO_ANIMATION_STATES.mileage;
  const loadingHandler = useCallback((val: HeroAnimationState) => setLoading(val), []);
  const hasError = vehicle?.resultError;

  useEffect(() => {
    if (isTransition) {
      history.push(`/${vehicle.vrm}`);
    }
  }, [isTransition]);

  const onSetShowMileage = (value: boolean) => {
    setShowMileage(value);

    if (value) {
      fireCookieConsentTakeover();
    }
  };

  return (
    <aside className={cx(styles.vrmBox, { [styles.error]: false })}>
      {
        hasError ? (
          <VrmErrors error={hasError?.msg} />
        ) : (
          <>
            {(affiliate && !showMileage) && (
              <div className={cx(styles.partnership, { [styles[affiliate]]: styles?.[affiliate] })}>
                {LocalT.translate('vrm.partnership')}
                <LogoAffiliate affiliate={affiliate} className={styles.logo} />
              </div>
            )}
            {
              showMileage ? (
                <div className={styles.vehicle}>
                  {vehicle?.vrm_pretty && <THCVRM text={vehicle?.vrm_pretty} />}
                  <h2>
                    {vehicle?.make?.display_name}
                    {' '}
                    {vehicle?.model}
                  </h2>
                </div>
              ) : (
                <>
                  <h3 className={styles.label}>
                    {LocalT.translate('vrm.title')}
                  </h3>
                  <p className={styles.subDesc}>
                    {LocalT.translate(affiliate === 'theaa' ? 'theaa.heroStrap' : 'heroStrap')}
                  </p>
                </>
              )
            }
          </>
        )
      }
      {!errorCodesToCheck.includes(hasError?.msg as CheckableErrorCode) && (
        <VrmInput
          maxWidth={CONTENT_SIZES.full}
          {...{
            loadingHandler,
            setShowMileage: onSetShowMileage,
            showMileage,
            vehicle,
          }}
        />
      )}
    </aside>
  );
};

export default VRM;
